import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { FINANCE_MOVEMENT_ORIGIN_TONE } from '@/lib/status-tone';
import { formatMinor } from '@/lib/money';
import type {
  FinanceAccount,
  FinanceCategory,
  FinanceCounterparty,
  FinanceMovement,
  FinanceMovementLine,
} from '@/api/generated/types.gen';

// «Fee» is the one name of the withheld kind (OH-212), as in the confirmation windows (SettleDraftPanels' KIND_TEXT).
const LINE_KIND_LABEL: Record<FinanceMovementLine['kind'], string> = {
  settle: 'Settles a booking',
  income: 'Income',
  expense: 'Expense',
  transfer_body: 'Transfer body',
  withheld: 'Fee',
  fx_rounding: 'FX rounding',
};

export interface MovementCardProps {
  movement: FinanceMovement;
  accounts?: readonly FinanceAccount[];
  categories?: readonly FinanceCategory[];
  counterparties?: readonly FinanceCounterparty[];
  apartments?: readonly { id: string; title: string }[];
  actions?: React.ReactNode;
}

export function MovementCard({
  movement: m,
  accounts,
  categories,
  counterparties,
  apartments,
  actions,
}: MovementCardProps) {
  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-serif text-2xl font-bold tabular-nums text-[var(--text-heading)]">
              {formatMinor(m.amount_minor, m.currency)}
              <span className="ml-2 text-sm font-sans font-normal text-muted">
                {m.direction === 'in' ? 'in' : 'out'}
              </span>
            </p>
            <p className="mt-1 text-sm text-muted">
              {accounts?.find((a) => a.id === m.account_id)?.name ?? m.account_id} · {m.occurred_on}
            </p>
          </div>
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge variant={FINANCE_MOVEMENT_ORIGIN_TONE[m.origin].variant}>
              {FINANCE_MOVEMENT_ORIGIN_TONE[m.origin].label}
            </Badge>
            {m.kind === 'transfer' && <Badge variant="navy">Transfer</Badge>}
            {m.reversed_by_id && <Badge variant="danger">Reversed</Badge>}
            {m.reverses_id && <Badge variant="warning">Reversal</Badge>}
            {actions}
          </span>
        </div>

        <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="EUR equivalent">{formatMinor(m.eur_minor, 'EUR')}</Row>
          <Row label="Rate">
            {/* fx_source=same means the header is already EUR and no rate was
                read at all (§6.2), so a rate of "—" there is the truth. */}
            {m.fx_rate_micro == null
              ? `${m.fx_source} — no rate read`
              : `${(m.fx_rate_micro / 1_000_000).toFixed(6)} ${m.currency}/EUR · ${m.fx_source}${
                  m.fx_rate_date ? ` · ${m.fx_rate_date}` : ''
                }`}
          </Row>
          {m.correction_for_month && (
            <Row label="Correction for">{m.correction_for_month.slice(0, 7)}</Row>
          )}
          {m.reversal_reason && <Row label="Reason">{m.reversal_reason}</Row>}
          {m.note && <Row label="Note">{m.note}</Row>}
          {m.import_ref && <Row label="Import reference">{m.import_ref}</Row>}
          <Row label="Recorded">{new Date(m.created_at).toISOString().replace('T', ' ').slice(0, 16)}</Row>
        </dl>

        {(m.reverses_id || m.reversed_by_id || m.transfer_id) && (
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[var(--border-hairline)] pt-3 text-sm">
            {m.reverses_id && (
              <Link
                to="/finance/movements/$id"
                params={{ id: m.reverses_id }}
                className="text-primary hover:underline"
              >
                The payment this cancels
              </Link>
            )}
            {m.reversed_by_id && (
              <Link
                to="/finance/movements/$id"
                params={{ id: m.reversed_by_id }}
                className="text-primary hover:underline"
              >
                The reversal that cancelled this
              </Link>
            )}
            {/* transfer_id is the pair's identity, not a page: no route and no
                filter takes it in slice 1, so it is shown rather than linked
                to somewhere that would 404. */}
            {m.transfer_id && (
              <span className="text-muted">
                Transfer <code className="text-xs">{m.transfer_id}</code>
              </span>
            )}
          </div>
        )}
      </Card>

      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border-hairline)] text-left text-xs uppercase tracking-[0.06em] text-muted">
              <th className="px-4 py-2.5 font-semibold">Line</th>
              <th className="px-4 py-2.5 font-semibold">Article</th>
              <th className="px-4 py-2.5 font-semibold">Amount</th>
              <th className="px-4 py-2.5 font-semibold">EUR</th>
              <th className="px-4 py-2.5 font-semibold">Where it belongs</th>
            </tr>
          </thead>
          <tbody>
            {m.lines.map((l) => (
              <tr key={l.id} className="border-t border-[var(--border-hairline)]">
                <td className="px-4 py-3">{LINE_KIND_LABEL[l.kind] ?? l.kind}</td>
                <td className="px-4 py-3">
                  {l.category_id
                    ? (categories?.find((c) => c.id === l.category_id)?.name ?? l.category_id)
                    : '—'}
                  {l.expense_type && <span className="ml-1 text-xs text-muted">{l.expense_type}</span>}
                </td>
                <td className="px-4 py-3 tabular-nums">
                  {/* A withheld line lowers the bank amount (equation.go gives
                      it −1), so it is printed with the sign it carries in the
                      header equation rather than as a bare positive. */}
                  {l.kind === 'withheld' ? '−' : ''}
                  {formatMinor(l.amount_minor, m.currency)}
                </td>
                <td className="px-4 py-3 tabular-nums text-muted">{formatMinor(l.eur_minor, 'EUR')}</td>
                <td className="px-4 py-3 text-muted">
                  {[
                    l.apartment_id
                      ? (apartments?.find((a) => a.id === l.apartment_id)?.title ?? l.apartment_id)
                      : null,
                    l.counterparty_id
                      ? (counterparties?.find((c) => c.id === l.counterparty_id)?.name ??
                        l.counterparty_id)
                      : null,
                    l.booking_id ? `booking ${l.booking_id.slice(0, 8)}` : null,
                    l.note,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 sm:block">
      <dt className="text-xs uppercase tracking-[0.06em] text-muted">{label}</dt>
      <dd className="tabular-nums">{children}</dd>
    </div>
  );
}
